import { FC, memo } from 'react'
import { Box, useToken, useColorModeValue } from '@chakra-ui/react'

type Props = {
  size?: string | number
}

const WithoutCameraIllustration: FC<Props> = ({ size = 60 }) => {
  const [primary100, primary500, primary700, gray50, gray900, red500] = useToken('colors', [
    'primary.100',
    'primary.500',
    'primary.700',
    'gray.50',
    'gray.900',
    'red.500',
  ])

  const bgColor = useColorModeValue(gray50, gray900)
  const strokeColor = useColorModeValue(primary500, primary700)
  const strokenWidth = '1020px'
  const fillBG = useColorModeValue(primary100, primary100)
  const fillLineRed = useColorModeValue(red500, red500)

  return (
    <Box w={size} h={size} pos="relative">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 569.9 468.81">
        <g id="BG">
          <path
            fill={bgColor}
            d="M646.9,533.26l-32.52-22.77-4.77-3.34A232.19,232.19,0,0,0,627.5,417.5c0-128.68-104.32-233-233-233a232,232,0,0,0-107.1,26c-12.44-8.81-24.7-18.21-37.6-26.3-.19-.12-.37-.27-.56-.4-6.64-4.65-16.67-1.2-20.52,5.38-4.38,7.49-1.28,15.87,5.38,20.53l25.44,17.81a233.92,233.92,0,0,0-22.21,18l-27.61-19.34c-13.25-9.27-26.22-19.35-39.92-27.94-.19-.12-.37-.27-.56-.4-6.64-4.65-16.67-1.2-20.52,5.38-4.38,7.49-1.28,15.87,5.38,20.53l32.67,22.88,29.6,20.72a234.18,234.18,0,0,0-16.44,22c-10.17-7.34-20.29-14.91-30.88-21.52l-.54-.39c-6.64-4.65-16.67-1.2-20.52,5.39-4.38,7.48-1.28,15.86,5.38,20.52l31.45,22,.18.13a231.9,231.9,0,0,0-23.5,102c0,128.68,104.32,233,233,233a232.27,232.27,0,0,0,166.76-70.3l18.18,12.74c13.19,9.23,26.11,19.28,39.75,27.83.2.12.38.27.57.4,6.64,4.65,16.67,1.2,20.52-5.38,4.38-7.49,1.28-15.87-5.38-20.53l-32.52-22.77L580.83,557.4a234.37,234.37,0,0,0,15.33-23.12c11.57,8.27,23,17,35,24.49.2.12.38.27.57.4,6.64,4.65,16.67,1.2,20.52-5.38C656.66,546.3,653.56,537.92,646.9,533.26Z"
            transform="translate(-97.05 -181.69)"
          />
          <path
            fill={bgColor}
            d="M136.22,247.12l-16.71-11.7c-6.45-4.51-17-1.38-20.52,5.39-4.06,7.68-1.5,15.7,5.38,20.52L121.08,273c6.44,4.51,17,1.38,20.52-5.38C145.66,260,143.1,251.94,136.22,247.12Z"
            transform="translate(-97.05 -181.69)"
          />
        </g>
        <g id="Stars">
          <g id="Star">
            <path
              fill={strokeColor}
              d="M274.5,561h20a1.5,1.5,0,0,0,0-3h-20a1.5,1.5,0,0,0,0,3Z"
              transform="translate(-97.05 -181.69)"
            />
            <path
              fill={strokeColor}
              d="M283,549.5v20a1.5,1.5,0,0,0,3,0v-20a1.5,1.5,0,0,0-3,0Z"
              transform="translate(-97.05 -181.69)"
            />
          </g>
          <g id="Star-2" data-name="Star">
            <path
              fill={strokeColor}
              d="M566.5,341h16a1.5,1.5,0,0,0,0-3h-16a1.5,1.5,0,0,0,0,3Z"
              transform="translate(-97.05 -181.69)"
            />
            <path
              fill={strokeColor}
              d="M573,331.5v16a1.5,1.5,0,0,0,3,0v-16a1.5,1.5,0,0,0-3,0Z"
              transform="translate(-97.05 -181.69)"
            />
          </g>
          <g id="Star-3" data-name="Star">
            <path
              fill={strokeColor}
              d="M408.5,211h12a1.5,1.5,0,0,0,0-3h-12a1.5,1.5,0,0,0,0,3Z"
              transform="translate(-97.05 -181.69)"
            />
            <path
              fill={strokeColor}
              d="M413,203.5v12a1.5,1.5,0,0,0,3,0v-12a1.5,1.5,0,0,0-3,0Z"
              transform="translate(-97.05 -181.69)"
            />
          </g>
          <g id="Star-4" data-name="Star">
            <path
              fill={strokeColor}
              d="M559.5,621h10a1.5,1.5,0,0,0,0-3h-10a1.5,1.5,0,0,0,0,3Z"
              transform="translate(-97.05 -181.69)"
            />
            <path
              fill={strokeColor}
              d="M563,614.5v10a1.5,1.5,0,0,0,3,0v-10a1.5,1.5,0,0,0-3,0Z"
              transform="translate(-97.05 -181.69)"
            />
          </g>
          <g id="Star-5" data-name="Star">
            <path
              fill={strokeColor}
              d="M369.5,611h10a1.5,1.5,0,0,0,0-3h-10a1.5,1.5,0,0,0,0,3Z"
              transform="translate(-97.05 -181.69)"
            />
            <path
              fill={strokeColor}
              d="M373,604.5v10a1.5,1.5,0,0,0,3,0v-10a1.5,1.5,0,0,0-3,0Z"
              transform="translate(-97.05 -181.69)"
            />
          </g>
          <g id="Star-6" data-name="Star">
            <path
              fill={strokeColor}
              d="M249.5,451h10a1.5,1.5,0,0,0,0-3h-10a1.5,1.5,0,0,0,0,3Z"
              transform="translate(-97.05 -181.69)"
            />
            <path
              fill={strokeColor}
              d="M253,444.5v10a1.5,1.5,0,0,0,3,0v-10a1.5,1.5,0,0,0-3,0Z"
              transform="translate(-97.05 -181.69)"
            />
          </g>
          <g id="Star-7" data-name="Star">
            <path
              fill={strokeColor}
              d="M643.5,441h22a1.5,1.5,0,0,0,0-3h-22a1.5,1.5,0,0,0,0,3Z"
              transform="translate(-97.05 -181.69)"
            />
            <path
              fill={strokeColor}
              d="M653,428.5v22a1.5,1.5,0,0,0,3,0v-22a1.5,1.5,0,0,0-3,0Z"
              transform="translate(-97.05 -181.69)"
            />
          </g>
          <g id="Star-8" data-name="Star">
            <path
              fill={strokeColor}
              d="M135.5,451h18a1.5,1.5,0,0,0,0-3h-18a1.5,1.5,0,0,0,0,3Z"
              transform="translate(-97.05 -181.69)"
            />
            <path
              fill={strokeColor}
              d="M143,440.5v18a1.5,1.5,0,0,0,3,0v-18a1.5,1.5,0,0,0-3,0Z"
              transform="translate(-97.05 -181.69)"
            />
          </g>
          <g id="Star-9" data-name="Star">
            <path
              fill={strokeColor}
              d="M564.5,221h20a1.5,1.5,0,0,0,0-3h-20a1.5,1.5,0,0,0,0,3Z"
              transform="translate(-97.05 -181.69)"
            />
            <path
              fill={strokeColor}
              d="M573,209.5v20a1.5,1.5,0,0,0,3,0v-20a1.5,1.5,0,0,0-3,0Z"
              transform="translate(-97.05 -181.69)"
            />
          </g>
          <g id="Star-10" data-name="Star">
            <path
              fill={strokeColor}
              d="M262.5,271h24a1.5,1.5,0,0,0,0-3h-24a1.5,1.5,0,0,0,0,3Z"
              transform="translate(-97.05 -181.69)"
            />
            <path
              fill={strokeColor}
              d="M273,257.5v24a1.5,1.5,0,0,0,3,0v-24a1.5,1.5,0,0,0-3,0Z"
              transform="translate(-97.05 -181.69)"
            />
          </g>
        </g>
        <g>
          <g>
            <Box
              as="circle"
              cx="301.48"
              cy="228.87"
              r="111.02"
              fill={fillBG}
              strokeWidth={strokenWidth}
              color={strokeColor}
            />
            <path
              className="cls-2"
              d="M400.5,228.87c-.25,41-26.39,79.26-64.85,93a99.87,99.87,0,0,1-110.77-30.19c-26.37-31.43-29.56-78.78-8-114.29,21.13-34.8,63.56-53.75,103.29-45.81,23.28,4.65,42.65,16.27,57.92,34.47A107.91,107.91,0,0,1,394,193.46a97.7,97.7,0,0,1,6.47,35.41c.1,15.43,24.1,15.47,24,0a124.57,124.57,0,0,0-81.23-115.7c-47.59-17.65-104-2.34-136.19,36.91-32.85,40.1-38,97.72-10.92,142.37,27,44.4,79.62,67,130.39,56.85,48.66-9.73,88.55-50.93,96.19-100a136.66,136.66,0,0,0,1.76-20.39C424.6,213.42,400.6,213.4,400.5,228.87Z"
              strokeWidth={strokenWidth}
              color={strokeColor}
              fill={strokeColor}
            />
          </g>

          <g>
            <Box
              as="circle"
              className="cls-3"
              cx="302.03"
              cy="228.32"
              r="52.5"
              fill={fillBG}
              color="red"
              stroke="red"
              strokeWidth="6px"
            />
            <path
              className="cls-2"
              d="M342.53,228.32c-.22,16.57-10.77,32.62-26.34,38-16.89,5.83-34.28,1.22-45.87-12.71-10.36-12.45-11.64-31.69-3-46.1s26.4-22.37,42.47-19a40.94,40.94,0,0,1,32.78,39.79c.2,15.43,24.2,15.47,24,0a65.51,65.51,0,0,0-42.71-60.68c-24.5-9.26-54.26-1.25-70.89,18.92-17.31,21-20.49,51-6.37,74.69s41.49,35.2,68.25,30.27c30-5.54,51.33-33.25,51.72-63.2C366.73,212.87,342.73,212.86,342.53,228.32Z"
              fill={strokeColor}
            />
          </g>
          <path
            d="M235.27,373.76c20.83,9.47,44.75,13.53,67.47,13.66,21.58.13,45.34-2.85,65-12.57,5.8-2.87,7.41-11.1,4.3-16.42-3.46-5.91-10.59-7.19-16.42-4.3-.32.16-.65.29-1,.45-2.12,1.05,2.95-1.1,1-.41-.53.19-1,.42-1.57.62a111.41,111.41,0,0,1-11.57,3.74,146.42,146.42,0,0,1-39.73,4.89,157.74,157.74,0,0,1-40.59-5.58c-4.27-1.16-8.5-2.48-12.65-4-1.18-.43-2.34-1-3.53-1.37-1.63-.57,3.38,1.48,1.41.58-5.89-2.68-12.91-1.68-16.41,4.31-3,5.15-1.63,13.72,4.3,16.42Z"
            fill={strokeColor}
          />
          <path
            d="M173,331.1l94.7-51.79,150.83-82.49L453,178c13.55-7.42,1.46-28.15-12.11-20.73L346.19,209,195.36,291.53,160.9,310.38c-13.56,7.41-1.46,28.14,12.11,20.72Z"
            fill={fillLineRed}
          />
          <circle cx="314.06" cy="213.01" r="8.2" fill={strokeColor} />
        </g>
      </svg>
    </Box>
  )
}

export default memo(WithoutCameraIllustration)

/* 


*/
